import { z } from 'zod'
import { optionalEmail, optionalUrl } from './fields'
import { MEDIA_MAX_SIZE } from './media'
import {
  type CreateWeddingSupplierContactInput,
  createWeddingSupplierContactSchema,
  type UpdateWeddingSupplierContactInput,
  updateWeddingSupplierContactSchema,
} from './supplier'
import { planningTimeSchema } from './supplier-plan'

export const VENUE_ROLES = [
  'ceremony',
  'reception',
  'accommodation',
  'photos',
  'preparations',
  'afterparty',
] as const

export const venueRoleSchema = z.enum(VENUE_ROLES)

const optionalText = (max: number) =>
  z.preprocess(
    (value) => (typeof value === 'string' && value.trim() === '' ? undefined : value),
    z.string().trim().max(max).optional(),
  )

const clearableText = (max: number) =>
  z.preprocess(
    (value) => (typeof value === 'string' && value.trim() === '' ? null : value),
    z.string().trim().max(max).nullable().optional(),
  )

const clearableDateTime = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? null : value),
  z.iso.datetime({ offset: true }).nullable().optional(),
)

const clearableTime = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? null : value),
  planningTimeSchema.nullable().optional(),
)

const clearableRoomsCount = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? null : value),
  z.number().int().min(0).max(1000).nullable().optional(),
)

const venueDetailsSchema = z.object({
  roles: z.array(venueRoleSchema).min(1),
  summary: optionalText(3000),
})

export const createVenueSchema = z.discriminatedUnion('source', [
  venueDetailsSchema.extend({
    source: z.literal('existing'),
    weddingSupplierId: z.uuid(),
  }),
  venueDetailsSchema.extend({
    source: z.literal('new'),
    name: z.string().trim().min(1).max(160),
    address: z.string().trim().min(1).max(300),
    email: optionalEmail,
    phone: optionalText(60),
    website: optionalUrl,
  }),
])

export const updateVenueSchema = z.object({
  roles: z.array(venueRoleSchema).min(1).optional(),
  summary: clearableText(3000),
  availableFrom: clearableDateTime,
  availableTo: clearableDateTime,
  handoverNote: clearableText(1000),
  coordinationMode: clearableText(120),
  coordinationOwner: clearableText(160),
  coordinationNote: clearableText(2000),
  includedItems: z.array(z.string().trim().min(1).max(300)).max(100).optional(),
  restrictions: z.array(z.string().trim().min(1).max(300)).max(100).optional(),
  parkingNote: clearableText(2000),
  arrivalNote: clearableText(2000),
  accessibilityNote: clearableText(2000),
  loadInFrom: clearableTime,
  loadInTo: clearableTime,
  loadInNote: clearableText(2000),
  powerSoundNote: clearableText(2000),
  roomsCount: clearableRoomsCount,
  checkIn: clearableTime,
  checkOut: clearableTime,
  roomsNote: clearableText(2000),
  notes: clearableText(5000),
  hiddenPromptCodes: z
    .array(z.string().trim().min(1).max(80))
    .max(100)
    .transform((codes) => [...new Set(codes)])
    .optional(),
})

export const createVenueContactSchema = createWeddingSupplierContactSchema
export const updateVenueContactSchema = updateWeddingSupplierContactSchema

export const createDocumentUploadSchema = z.object({
  originalName: z.string().trim().min(1).max(255),
  mimeType: z.string().trim().min(1).max(160),
  size: z.number().int().positive().max(MEDIA_MAX_SIZE.document),
  checksum: optionalText(128),
})

export const completeDocumentUploadSchema = z.object({ checksum: optionalText(128) })

export type VenueRole = (typeof VENUE_ROLES)[number]
export type CreateVenueInput = z.infer<typeof createVenueSchema>
export type UpdateVenueInput = z.infer<typeof updateVenueSchema>
export type CreateVenueContactInput = CreateWeddingSupplierContactInput
export type UpdateVenueContactInput = UpdateWeddingSupplierContactInput
export type CreateDocumentUploadInput = z.infer<typeof createDocumentUploadSchema>
export type CompleteDocumentUploadInput = z.infer<typeof completeDocumentUploadSchema>
